/*
 * Repello same-page pack selection.
 *
 * One <repello-pack-selection> element wraps each eligible main-product section. It is rendered by
 * sections/main-product.liquid only when the `repello_same_page_pack_selection` theme setting is on and
 * the product is one of the seven contracted Repello Pack PDPs or a configured larger-pack product.
 *
 * The element lets the existing pack selector switch between the Liquid-resolved 1-, 2- and 3-pack
 * offers without leaving the page, keeps the existing buy button on the committed offer, and serialises
 * purchases through one lock. Liquid owns offer identity and money (snippets/repello-pack-option-data.liquid);
 * this file never resolves products, computes prices or calls the cart. The existing buy buttons keep their
 * own transport and ask this element for an immutable submission snapshot.
 *
 * Until the element has validated its offer data and every purchase surface in its section, it stays in
 * navigation mode and each pack option keeps linking to that pack's own product page.
 *
 * Plan: docs/plans/2026-09-10-1451-feat-same-page-pack-selection-plan.md
 */
(() => {
  if (window.RepelloPackSelection) {
    return;
  }

  const SCHEMA_VERSION = 1;
  const EXTENDED_SCHEMA_VERSION = 2;
  const PACK_OPTIONS = ['1', '2', '3'];
  const EXTENDED_QUANTITIES = ['1', '2', '3', '4', '5', '6', '7', '8'];
  const SUBMISSION_TIMEOUT_MS = 20000;

  // Purchase triggers this element cannot point at the selected offer. Any of them in the section could
  // still buy the entry product after another pack is selected, so their presence keeps navigation mode.
  const UNCONTROLLED_PURCHASE_SELECTOR = 'product-quick-add, buy-buttons, .shopify-payment-button, shopify-accelerated-checkout';

  // Entry-variant displays this element does not update.
  // Impact's <payment-terms> wrapper is always rendered; Shopify's financing copy, when offered, is the shopify-payment-terms element matched here.
  const UNCONTROLLED_DISPLAY_SELECTOR = 'price-list, variant-inventory, shopify-payment-terms';

  class OfferDataError extends Error {
    constructor(message) {
      super(message);
      this.name = 'OfferDataError';
    }
  }

  const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';
  const isIdString = (value) => typeof value === 'string' && /^[1-9]\d*$/.test(value);
  const isPositiveMoney = (value) => Number.isSafeInteger(value) && value > 0;

  function parseOffer(raw, family, quantities) {
    const fail = (reason) => {
      throw new OfferDataError(`Offer ${isPlainObject(raw) ? raw.variantId : '?'} ${reason}.`);
    };

    if (!isPlainObject(raw)) fail('is not an object');
    if (!isIdString(raw.productId)) fail('has no product ID');
    if (!isIdString(raw.variantId)) fail('has no variant ID');
    if (raw.family !== family) fail(`belongs to family "${raw.family}", not "${family}"`);
    if (!Number.isInteger(raw.packSize) || !quantities.includes(String(raw.packSize))) fail('has an invalid pack size');
    if (raw.cartQuantity !== 1) fail('must be added with cart quantity 1');
    if (!isNonEmptyString(raw.url) || !raw.url.startsWith('/')) fail('has no storefront URL');
    if (typeof raw.available !== 'boolean') fail('has no availability');
    if (!isPositiveMoney(raw.price)) fail('has no valid price');
    if (raw.compareAtPrice !== null && !(Number.isSafeInteger(raw.compareAtPrice) && raw.compareAtPrice >= 0)) fail('has an invalid compare-at price');
    if (!isNonEmptyString(raw.priceFormatted) || !isNonEmptyString(raw.perDevicePriceFormatted)) fail('has no formatted price');

    const hasSaving = raw.compareAtPrice !== null && raw.compareAtPrice > raw.price;

    if (hasSaving) {
      if (!isNonEmptyString(raw.compareAtPriceFormatted) || !isNonEmptyString(raw.savingFormatted)) fail('has a saving without its formatted amounts');
    } else if (raw.compareAtPriceFormatted !== null || raw.savingFormatted !== null) {
      fail('shows a saving its compare-at price does not support');
    }

    for (const field of ['title', 'ctaLabel', 'announcement']) {
      if (!isNonEmptyString(raw[field])) fail(`has no ${field}`);
    }

    if (raw.availabilityLabel !== undefined && typeof raw.availabilityLabel !== 'string') fail('has an invalid availability label');
    if (raw.unitPriceLabel !== undefined && !isNonEmptyString(raw.unitPriceLabel)) fail('has an invalid unit-price label');

    return Object.freeze({
      key: raw.variantId,
      productId: raw.productId,
      variantId: raw.variantId,
      family: raw.family,
      packSize: raw.packSize,
      cartQuantity: raw.cartQuantity,
      url: raw.url,
      available: raw.available,
      price: raw.price,
      compareAtPrice: raw.compareAtPrice,
      priceFormatted: raw.priceFormatted,
      compareAtPriceFormatted: raw.compareAtPriceFormatted,
      savingFormatted: raw.savingFormatted,
      perDevicePriceFormatted: raw.perDevicePriceFormatted,
      title: raw.title,
      ctaLabel: raw.ctaLabel,
      announcement: raw.announcement,
      availabilityLabel: raw.availabilityLabel ?? '',
      unitPriceLabel: raw.unitPriceLabel ?? raw.perDevicePriceFormatted
    });
  }

  /**
   * Validates the Liquid offer payload and returns an immutable model, or throws OfferDataError.
   * Transitions are taken as emitted by the Liquid resolver; they are never inferred here.
   */
  function parseOfferData(payload) {
    if (!isPlainObject(payload)) throw new OfferDataError('Offer payload is not an object.');
    if (payload.schema !== SCHEMA_VERSION && payload.schema !== EXTENDED_SCHEMA_VERSION) {
      throw new OfferDataError(`Offer payload schema ${payload.schema} is not supported.`);
    }
    if (payload.family !== 'rat' && payload.family !== 'mouse') throw new OfferDataError(`Offer family "${payload.family}" is unknown.`);
    if (!isPlainObject(payload.context) || !isNonEmptyString(payload.context.currency)) throw new OfferDataError('Offer payload has no currency context.');
    if (!Array.isArray(payload.offers) || payload.offers.length === 0) throw new OfferDataError('Offer payload has no offers.');

    const quantities = payload.schema === EXTENDED_SCHEMA_VERSION ? EXTENDED_QUANTITIES : PACK_OPTIONS;
    const offers = new Map();
    const productQuantities = new Map();
    const quantityCounts = new Map();

    for (const raw of payload.offers) {
      const offer = parseOffer(raw, payload.family, quantities);
      const quantity = String(offer.packSize);

      if (offers.has(offer.key)) throw new OfferDataError(`Variant ${offer.variantId} is offered twice.`);
      if (payload.schema === SCHEMA_VERSION && productQuantities.has(offer.productId)) throw new OfferDataError(`Product ${offer.productId} is offered twice.`);
      if (
        payload.schema === EXTENDED_SCHEMA_VERSION &&
        productQuantities.has(offer.productId) &&
        (offer.packSize < 4 || productQuantities.get(offer.productId) < 4)
      ) {
        throw new OfferDataError(`Product ${offer.productId} is offered for more than one established pack identity.`);
      }
      if (payload.schema === EXTENDED_SCHEMA_VERSION && offer.packSize !== 1 && quantityCounts.has(quantity)) {
        throw new OfferDataError(`Quantity ${offer.packSize} is offered twice.`);
      }

      offers.set(offer.key, offer);
      productQuantities.set(offer.productId, offer.packSize);
      quantityCounts.set(quantity, (quantityCounts.get(quantity) ?? 0) + 1);
    }

    if (
      payload.schema === EXTENDED_SCHEMA_VERSION &&
      new Set([...offers.values()].filter(({ packSize }) => packSize >= 4).map(({ productId }) => productId)).size !== 1
    ) {
      throw new OfferDataError('Extended quantities 4 through 8 must share one product within the active family.');
    }

    if (payload.schema === EXTENDED_SCHEMA_VERSION && [...quantityCounts.keys()].sort().join() !== quantities.join()) {
      throw new OfferDataError(`Offer payload does not contain every quantity for schema ${payload.schema}.`);
    }

    if (payload.schema === EXTENDED_SCHEMA_VERSION && (quantityCounts.get('1') > 2 || quantities.slice(1).some((quantity) => quantityCounts.get(quantity) !== 1))) {
      throw new OfferDataError('Extended offer quantities are ambiguous.');
    }

    if (!offers.has(payload.entry)) throw new OfferDataError(`Entry offer ${payload.entry} is missing.`);
    if (!isPlainObject(payload.transitions)) throw new OfferDataError('Offer payload has no transitions.');

    const transitions = new Map();

    for (const [fromKey, targets] of Object.entries(payload.transitions)) {
      if (!offers.has(fromKey)) throw new OfferDataError(`Transitions start from unknown offer ${fromKey}.`);
      if (!isPlainObject(targets)) throw new OfferDataError(`Transitions from ${fromKey} are malformed.`);

      const row = new Map();

      for (const [option, toKey] of Object.entries(targets)) {
        const target = offers.get(toKey);

        if (!quantities.includes(option)) throw new OfferDataError(`Option "${option}" is not a pack option.`);
        if (!target) throw new OfferDataError(`Option ${option} from ${fromKey} targets unknown offer ${toKey}.`);
        if (String(target.packSize) !== option) throw new OfferDataError(`Option ${option} from ${fromKey} targets a ${target.packSize}-pack.`);

        row.set(option, toKey);
      }

      transitions.set(fromKey, row);
    }

    for (const key of offers.keys()) {
      if (!transitions.has(key)) throw new OfferDataError(`Offer ${key} has no transitions.`);
    }

    const entryOffer = offers.get(payload.entry);
    const transitionQuantities = Object.freeze([...transitions.get(payload.entry).keys()].sort());

    for (const [fromKey, row] of transitions) {
      if ([...row.keys()].sort().join() !== transitionQuantities.join()) throw new OfferDataError(`Offer ${fromKey} exposes different options from the entry offer.`);
    }

    if (payload.schema === EXTENDED_SCHEMA_VERSION && transitionQuantities.join() !== quantities.join()) {
      throw new OfferDataError('Offer transitions do not cover every quantity.');
    }

    if (transitions.get(payload.entry).get(String(entryOffer.packSize)) !== payload.entry) {
      throw new OfferDataError('The entry offer is not selected by its own pack option.');
    }

    return Object.freeze({
      family: payload.family,
      entry: payload.entry,
      schema: payload.schema,
      offers,
      transitions,
      options: Object.freeze(payload.schema === EXTENDED_SCHEMA_VERSION ? [...PACK_OPTIONS] : [...transitionQuantities]),
      quantities: Object.freeze(payload.schema === EXTENDED_SCHEMA_VERSION ? [...quantities] : [...transitionQuantities]),
      context: Object.freeze({ ...payload.context })
    });
  }

  function resolveTransition(model, fromKey, option) {
    return model.transitions.get(fromKey)?.get(option) ?? null;
  }

  /**
   * Maps a cart request result to an outcome. A request that may have reached Shopify is never
   * reported as failed: only a response in the 4xx range or a failure while already offline is.
   */
  function classifySubmission(result = {}) {
    if (result.networkError) {
      return result.offlineAtStart ? 'rejected' : 'uncertain';
    }

    if (result.ok === true) {
      return 'success';
    }

    if (Number.isInteger(result.status) && result.status >= 400 && result.status < 500) {
      return 'rejected';
    }

    return 'uncertain';
  }

  /**
   * DOM-free selection and submission state for one section.
   * phase: 'ready' | 'submitting' | 'uncertain'. Selection and new submissions are only accepted in 'ready'.
   */
  function createSelectionState(model) {
    let selectedKey = model.entry;
    let thirdQuantity = model.offers.get(model.entry).packSize >= 3 ? model.offers.get(model.entry).packSize : 3;
    let phase = 'ready';
    let notice = null;
    let snapshot = null;

    const currentOffer = () => model.offers.get(selectedKey);

    return {
      get phase() {
        return phase;
      },

      get notice() {
        return notice;
      },

      get offer() {
        return currentOffer();
      },

      get visibleOption() {
        return currentOffer().packSize >= 3 ? '3' : String(currentOffer().packSize);
      },

      get thirdQuantity() {
        return thirdQuantity;
      },

      select(option) {
        const quantity = model.schema === EXTENDED_SCHEMA_VERSION && option === '3' ? String(thirdQuantity) : option;

        return this.selectQuantity(quantity);
      },

      selectQuantity(quantity) {
        if (phase !== 'ready') {
          return { status: 'locked', offer: currentOffer() };
        }

        if (!model.quantities.includes(quantity)) {
          return { status: 'invalid' };
        }

        const targetKey = resolveTransition(model, selectedKey, quantity);

        if (!targetKey) {
          return { status: 'invalid' };
        }

        const target = model.offers.get(targetKey);

        if (target.packSize >= 3) {
          thirdQuantity = target.packSize;
        }

        if (targetKey === selectedKey) {
          return { status: 'unchanged', offer: currentOffer() };
        }

        selectedKey = targetKey;
        notice = null;

        return { status: 'changed', offer: currentOffer() };
      },

      canStepThird(delta) {
        if (phase !== 'ready' || (delta !== -1 && delta !== 1)) {
          return false;
        }

        const nextValue = thirdQuantity + delta;
        const nextQuantity = String(nextValue);
        if (nextValue < 3 || nextValue > 8) return false;
        const targetKey = model.quantities.includes(nextQuantity) ? resolveTransition(model, selectedKey, nextQuantity) : null;

        return targetKey !== null && model.offers.get(targetKey)?.available === true;
      },

      stepThird(delta) {
        if (phase !== 'ready') {
          return { status: 'locked', offer: currentOffer() };
        }

        if (delta !== -1 && delta !== 1) {
          return { status: 'invalid', offer: currentOffer() };
        }

        const nextValue = thirdQuantity + delta;
        const nextQuantity = String(nextValue);

        if (nextValue < 3 || nextValue > 8 || !model.quantities.includes(nextQuantity)) {
          return { status: 'bounded', offer: currentOffer() };
        }

        const targetKey = resolveTransition(model, selectedKey, nextQuantity);
        const target = targetKey ? model.offers.get(targetKey) : null;

        if (!target) {
          return { status: 'invalid', offer: currentOffer() };
        }

        if (!target.available) {
          return { status: 'unavailable', offer: currentOffer(), unavailableOffer: target };
        }

        return this.selectQuantity(nextQuantity);
      },

      beginSubmission() {
        const offer = currentOffer();

        if (phase !== 'ready' || !offer.available) {
          return null;
        }

        phase = 'submitting';
        notice = null;
        snapshot = Object.freeze({
          key: offer.key,
          productId: offer.productId,
          variantId: offer.variantId,
          quantity: offer.cartQuantity
        });

        return snapshot;
      },

      /** Returns the outcome; the state only changes when `submission` is the one in flight. */
      settle(submission, result) {
        const outcome = classifySubmission(result);

        if (phase !== 'submitting' || submission !== snapshot) {
          return { outcome, applied: false };
        }

        snapshot = null;
        phase = outcome === 'uncertain' ? 'uncertain' : 'ready';
        notice = outcome === 'uncertain' ? 'uncertain' : null;

        return { outcome, applied: true };
      },

      /** Releases the lock after an uncertain result. The notice stays until the selection changes or a new purchase starts. */
      resume() {
        if (phase !== 'uncertain') {
          return false;
        }

        phase = 'ready';

        return true;
      },

      /** A page restored mid-request cannot know whether that request succeeded. */
      interrupt() {
        if (phase !== 'submitting') {
          return false;
        }

        phase = 'uncertain';
        notice = 'uncertain';
        snapshot = null;

        return true;
      }
    };
  }

  /**
   * The pack option a 2- or 3-pack collection card asked for with ?pack=N (snippets/native-bundle-target.liquid), or
   * null when the query names no option this page offers.
   */
  function requestedPackOption(search, options, entryPackSize = 1) {
    if (entryPackSize >= 4) {
      return null;
    }

    const requested = new URLSearchParams(search).get('pack');

    return requested !== null && options.includes(requested) ? requested : null;
  }

  window.RepelloPackSelection = Object.freeze({
    core: Object.freeze({
      SCHEMA_VERSION,
      OfferDataError,
      parseOfferData,
      resolveTransition,
      classifySubmission,
      createSelectionState,
      requestedPackOption
    })
  });

  if (!window.customElements || window.customElements.get('repello-pack-selection')) {
    return;
  }

  // Impact's custom-button moves its label into a child <div> when it upgrades.
  const labelElementOf = (button) => button.querySelector(':scope > div') || button;

  class RepelloPackSelectionElement extends HTMLElement {
    #abortController = null;
    #state = null;
    #parts = null;
    #original = null;

    get isEnhanced() {
      return this.#state !== null;
    }

    connectedCallback() {
      this.#abortController = new AbortController();

      try {
        this.#enhance();
      } catch (error) {
        this.#disableEnhancement(error);
        return;
      }

      const { signal } = this.#abortController;

      this.addEventListener('pack-selection:change', this.#onSelectionRequest, { signal });
      this.addEventListener('mousedown', this.#onMouseDown, { signal });
      this.addEventListener('keydown', this.#onKeyDown, { signal });
      this.addEventListener('click', this.#onClick, { signal });
      window.addEventListener('pageshow', this.#onPageShow, { signal });
      this.setAttribute('state', 'ready');
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#parts?.noticeElement?.remove();
      this.#state = null;
      this.#parts = null;
      this.#original = null;
    }

    /**
     * Called by the existing buy buttons. Returns null when a purchase must not start (one is in flight,
     * an uncertain result is unresolved, or the offer is unavailable); otherwise an immutable snapshot of
     * the committed offer and a settle() callback that must receive the request result. settle() returns the
     * outcome, or 'stale' for a result that no longer applies (the page was restored while it was in flight).
     */
    beginSubmission() {
      const state = this.#state;

      if (!state) {
        return null;
      }

      const snapshot = state.beginSubmission();
      this.#render();

      if (!snapshot) {
        return null;
      }

      const offlineAtStart = navigator.onLine === false;
      const timeout = new AbortController();
      const timer = window.setTimeout(() => timeout.abort(), SUBMISSION_TIMEOUT_MS);

      return Object.freeze({
        variantId: snapshot.variantId,
        quantity: snapshot.quantity,
        signal: timeout.signal,
        settle: (result) => {
          window.clearTimeout(timer);

          const { outcome, applied } = state.settle(snapshot, { ...result, offlineAtStart });

          // Re-render even a stale result: the buy button has just written its own busy state.
          if (this.#state === state) {
            this.#render();

            if (applied && outcome === 'uncertain') {
              this.#focusNoticeIfFocusWasLost();
            }
          }

          return applied ? outcome : 'stale';
        }
      });
    }

    #enhance() {
      const payloads = this.querySelectorAll('script[type="application/json"][data-repello-pack-offers]');

      if (payloads.length !== 1) {
        throw new OfferDataError(`Expected one offer payload, found ${payloads.length}.`);
      }

      let payload;

      try {
        payload = JSON.parse(payloads[0].textContent);
      } catch {
        throw new OfferDataError('Offer payload is not valid JSON.');
      }

      const model = parseOfferData(payload);

      if (model.offers.get(model.entry).productId !== this.getAttribute('entry-product-id')) {
        throw new OfferDataError('Offer payload does not belong to this product.');
      }

      const activeCurrency = window.Shopify?.currency?.active;

      if (activeCurrency && activeCurrency !== model.context.currency) {
        throw new OfferDataError(`Offers are priced in ${model.context.currency}, the storefront in ${activeCurrency}.`);
      }

      const selector = payloads[0].closest('pack-offer-selector, native-bundle-offer-selector');

      if (!selector || !this.contains(selector)) {
        throw new OfferDataError('Offer payload is not inside a pack selector.');
      }

      const radios = new Map();

      for (const input of selector.querySelectorAll('input[type="radio"]')) {
        if (radios.has(input.value)) {
          throw new OfferDataError(`Pack option ${input.value} is rendered twice.`);
        }

        radios.set(input.value, input);
      }

      if ([...radios.keys()].sort().join() !== model.options.join()) {
        throw new OfferDataError('Rendered pack options do not match the offer transitions.');
      }

      const buyButtons = this.querySelectorAll('pack-buy-button, native-bundle-buy-button');
      const buyButton = buyButtons.length === 1 ? buyButtons[0] : null;
      const button = buyButton?.querySelector('button');

      if (!button) {
        throw new OfferDataError(`Expected one pack buy button, found ${buyButtons.length}.`);
      }

      const section = this.closest('.shopify-section') || this;
      const uncontrolled = section.querySelector(UNCONTROLLED_PURCHASE_SELECTOR) || this.querySelector(UNCONTROLLED_DISPLAY_SELECTOR);

      if (uncontrolled) {
        throw new OfferDataError(`<${uncontrolled.localName}> is not controlled by pack selection.`);
      }

      const productFormId = this.getAttribute('product-form-id');
      const productForm = productFormId ? document.getElementById(productFormId) : null;

      if (!productForm || productForm.localName !== 'form' || !this.contains(productForm)) {
        throw new OfferDataError('The product form is not owned by this section.');
      }

      const installmentForm = document.getElementById(`${productFormId}-product-installment-form`);
      const idInputs = [productForm, installmentForm]
        .filter((form) => form && this.contains(form))
        .flatMap((form) => Array.from(form.querySelectorAll('input[name="id"]')));

      const status = this.querySelector('[data-repello-pack-status]');
      const noticeTemplate = this.querySelector('template[data-repello-pack-uncertain-template]');

      if (!status || !noticeTemplate?.content.firstElementChild) {
        throw new OfferDataError('Pack selection status markup is missing.');
      }

      let extendedParts = null;

      if (model.schema === EXTENDED_SCHEMA_VERSION) {
        const extended = selector.querySelector('[data-repello-pack-extended]');
        const thirdCard = selector.querySelector('[data-repello-pack-third-card]');
        const thirdTitle = selector.querySelector('[data-repello-pack-third-title]');
        const thirdTotal = selector.querySelector('[data-repello-pack-third-total]');
        const thirdPrice = thirdTotal?.querySelector('[data-repello-pack-third-price]');
        const thirdCompare = thirdTotal?.querySelector('[data-repello-pack-third-compare]');
        const thirdCompareValue = thirdCompare?.querySelector('[data-repello-pack-third-compare-value]');
        const thirdUnit = selector.querySelector('[data-repello-pack-third-unit]');
        const count = selector.querySelector('[data-repello-pack-count]');
        const availability = selector.querySelector('[data-repello-pack-availability]');
        const minus = selector.querySelector('[data-repello-pack-step="-1"]');
        const plus = selector.querySelector('[data-repello-pack-step="1"]');

        if (!extended || !thirdCard || !thirdTitle || !thirdTotal || !thirdPrice || !thirdCompare || !thirdCompareValue || !thirdUnit || !count || !availability || !minus || !plus) {
          throw new OfferDataError('Extended pack controls are incomplete.');
        }

        extendedParts = { extended, thirdCard, thirdTitle, thirdTotal, thirdPrice, thirdCompare, thirdCompareValue, thirdUnit, count, availability, minus, plus };
      }

      this.#original = {
        variantId: buyButton.getAttribute('variant-id'),
        quantity: buyButton.getAttribute('quantity'),
        label: labelElementOf(button).textContent,
        disabled: button.disabled,
        idValues: idInputs.map((input) => input.value),
        thirdTitle: extendedParts?.thirdTitle.innerHTML,
        thirdTotal: extendedParts?.thirdTotal.innerHTML,
        thirdUnit: extendedParts?.thirdUnit.innerHTML,
        extendedHidden: extendedParts?.extended.hidden
      };
      this.#parts = { selector, radios, buyButton, button, idInputs, status, noticeTemplate, noticeElement: null, extendedParts, model };
      this.#state = createSelectionState(model);

      // A 2- or 3-pack collection card links here with ?pack=N. Start on that pack as if the customer had chosen it,
      // before the first render so nothing is announced. The URL is left as it is.
      const requestedOption = requestedPackOption(window.location.search, model.options, model.offers.get(model.entry).packSize);

      if (requestedOption) {
        this.#state.select(requestedOption);
      }

      this.#render();
    }

    /** Writes the committed offer to every owned control in one synchronous pass. */
    #render() {
      const { radios, buyButton, button, idInputs } = this.#parts;
      const state = this.#state;
      const offer = state.offer;
      const selectedOption = state.visibleOption;
      const label = labelElementOf(button);

      for (const [option, input] of radios) {
        input.checked = option === selectedOption;
        input.disabled = state.phase !== 'ready';
      }

      buyButton.setAttribute('variant-id', offer.variantId);
      buyButton.setAttribute('quantity', String(offer.cartQuantity));

      if (label.textContent.trim() !== offer.ctaLabel) {
        label.textContent = offer.ctaLabel;
      }

      // While a request is in flight the buy button owns its busy state.
      if (state.phase !== 'submitting') {
        button.disabled = state.phase === 'uncertain' || !offer.available;
      }

      for (const input of idInputs) {
        input.value = offer.variantId;
      }

      const extended = this.#parts.extendedParts;

      if (extended) {
        const thirdKey = resolveTransition(this.#parts.model, offer.key, String(state.thirdQuantity));
        const thirdOffer = this.#parts.model.offers.get(thirdKey);
        const nextOffers = [-1, 1].map((delta) => {
          const nextValue = state.thirdQuantity + delta;

          if (nextValue < 3 || nextValue > 8) {
            return null;
          }

          const quantity = String(nextValue);
          const key = resolveTransition(this.#parts.model, offer.key, quantity);
          return key ? this.#parts.model.offers.get(key) : null;
        });
        const unavailable = !offer.available && offer.packSize >= 3
          ? offer
          : nextOffers.find((candidate) => candidate && !candidate.available);

        extended.extended.hidden = false;
        this.#parts.selector.setAttribute('extended', '');
        extended.thirdCard.toggleAttribute('data-selected', selectedOption === '3');
        extended.thirdTitle.textContent = thirdOffer.title;
        extended.thirdPrice.textContent = thirdOffer.priceFormatted;
        const comparisonPrice = thirdOffer.compareAtPriceFormatted;
        extended.thirdCompareValue.textContent = comparisonPrice ?? '';
        extended.thirdCompare.hidden = comparisonPrice === null;
        extended.thirdUnit.textContent = thirdOffer.unitPriceLabel;
        extended.count.textContent = String(state.thirdQuantity);
        extended.availability.textContent = unavailable?.availabilityLabel ?? '';
        extended.minus.disabled = state.phase !== 'ready';
        extended.plus.disabled = state.phase !== 'ready';
        extended.minus.setAttribute('aria-disabled', String(!state.canStepThird(-1)));
        extended.plus.setAttribute('aria-disabled', String(!state.canStepThird(1)));
      }

      this.#renderNotice();
    }

    #renderNotice() {
      const parts = this.#parts;

      if (this.#state.notice !== 'uncertain') {
        parts.noticeElement?.remove();
        parts.noticeElement = null;
        return;
      }

      if (!parts.noticeElement) {
        parts.noticeElement = parts.noticeTemplate.content.firstElementChild.cloneNode(true);
        parts.buyButton.after(parts.noticeElement);
      }

      const resumeButton = parts.noticeElement.querySelector('[data-repello-pack-resume]');

      if (resumeButton) {
        resumeButton.hidden = this.#state.phase !== 'uncertain';
      }
    }

    #focusNoticeIfFocusWasLost() {
      const active = document.activeElement;

      if (!active || active === document.body || this.#parts.buyButton.contains(active)) {
        this.#parts.noticeElement?.focus();
      }
    }

    #onSelectionRequest = (event) => {
      if (!this.#parts?.selector.contains(event.target)) {
        return;
      }

      const option = String(event.detail?.option ?? '');
      const result = this.#state.select(option);

      if (result.status === 'invalid') {
        // Not cancelled: the selector falls back to that option's own product page.
        this.#disableEnhancement(new OfferDataError(`No offer for pack option "${option}".`));
        return;
      }

      event.preventDefault();
      this.#render();

      if (result.status === 'changed') {
        this.#parts.status.textContent = result.offer.announcement;
      }
    };

    // Clicking an option focuses its visually hidden radio, and in this theme's layout that focus scrolls the page
    // (the live navigation storefront does the same, hidden by the page load). Focus the radio first without
    // scrolling; the label's own activation then selects it in place.
    #onMouseDown = (event) => {
      const input = event.button === 0 && event.target instanceof Element ? event.target.closest('label')?.control : null;

      if (!input || input.disabled || this.#parts?.radios.get(input.value) !== input) {
        return;
      }

      event.preventDefault();
      input.focus({ preventScroll: true });
    };

    // Arrow keys move a native radio group's focus without preventScroll, which scrolls the page for the same
    // reason as a click does. Move focus without scrolling and check the next option by clicking it, so keyboard
    // selection takes the same change path as a pointer.
    #onKeyDown = (event) => {
      const steps = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
      let step = steps[event.key];

      if (!step || event.altKey || event.ctrlKey || event.metaKey || !this.#parts) {
        return;
      }

      const radios = [...this.#parts.radios.values()];
      const index = radios.indexOf(event.target);

      if (index === -1 || event.target.disabled) {
        return;
      }

      if (document.documentElement.dir === 'rtl' && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        step = -step;
      }

      event.preventDefault();

      const next = radios[(index + step + radios.length) % radios.length];
      next.focus({ preventScroll: true });

      if (!next.checked) {
        next.click();
      }
    };

    #onClick = (event) => {
      const stepButton = event.target instanceof Element ? event.target.closest('[data-repello-pack-step]') : null;

      if (stepButton && this.#parts?.extendedParts?.extended.contains(stepButton)) {
        event.preventDefault();
        event.stopPropagation();

        if (stepButton.getAttribute('aria-disabled') === 'true') {
          stepButton.focus({ preventScroll: true });
          return;
        }

        const result = this.#state.stepThird(Number(stepButton.dataset.repelloPackStep));
        this.#render();

        if (!stepButton.disabled) {
          stepButton.focus({ preventScroll: true });
        }

        if (result.status === 'changed') {
          this.#parts.status.textContent = result.offer.announcement;
        } else if (result.status === 'unavailable') {
          this.#parts.status.textContent = result.unavailableOffer.availabilityLabel;
        }

        return;
      }

      const resumeButton = event.target instanceof Element ? event.target.closest('[data-repello-pack-resume]') : null;

      if (!resumeButton || !this.#parts?.noticeElement?.contains(resumeButton) || !this.#state.resume()) {
        return;
      }

      this.#render();
      this.#parts.radios.get(this.#state.visibleOption)?.focus();
    };

    #onPageShow = (event) => {
      if (!event.persisted || !this.#state) {
        return;
      }

      this.#state.interrupt();
      this.#render();
    };

    /** Returns the section to plain navigation, restoring the entry product on every owned control. */
    #disableEnhancement(error) {
      this.#abortController?.abort();

      const parts = this.#parts;
      const original = this.#original;

      if (parts && original) {
        parts.buyButton.setAttribute('variant-id', original.variantId);
        parts.buyButton.setAttribute('quantity', original.quantity);
        labelElementOf(parts.button).textContent = original.label;
        parts.button.disabled = original.disabled;
        parts.idInputs.forEach((input, index) => {
          input.value = original.idValues[index];
        });
        parts.radios.forEach((input) => {
          input.disabled = false;
        });
        if (parts.extendedParts) {
          parts.extendedParts.thirdTitle.innerHTML = original.thirdTitle;
          parts.extendedParts.thirdTotal.innerHTML = original.thirdTotal;
          parts.extendedParts.thirdUnit.innerHTML = original.thirdUnit;
          parts.extendedParts.extended.hidden = original.extendedHidden;
          parts.extendedParts.thirdCard.removeAttribute('data-selected');
          parts.selector.removeAttribute('extended');
        }
        parts.noticeElement?.remove();
      }

      this.#state = null;
      this.#parts = null;
      this.#original = null;
      this.setAttribute('state', 'navigation');

      console.warn('[repello-pack-selection] Pack options will open their own product pages:', error instanceof Error ? error.message : error);
    }
  }

  window.customElements.define('repello-pack-selection', RepelloPackSelectionElement);
})();
